import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenants } from '../hooks/useTenants';
import { useChatNotifications } from '../contexts/ChatNotificationsContext';

type InboxLite = {
  provider?: string;
  externalAccountId?: string;
  metadata?: Record<string, unknown> | null;
};

function makeBeeper() {
  let ctx: AudioContext | null = null;
  let lastAt = 0;
  return () => {
    const now = Date.now();
    if (now - lastAt < 1500) return;
    lastAt = now;
    try {
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;
      if (!ctx) ctx = new AudioContextClass();
      if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
      osc.start();
      osc.stop(ctx.currentTime + 0.34);
    } catch {
      // sem audio
    }
  };
}

const beep = makeBeeper();

export function MultiAccountNotifier() {
  const { token } = useAuth();
  const { tenants, currentTenant } = useTenants();
  const { addNotification, clearConversation } = useChatNotifications();
  const location = useLocation();

  const pathnameRef = useRef<string>(location.pathname);
  const addNotifRef = useRef(addNotification);
  const clearConvRef = useRef(clearConversation);

  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);
  useEffect(() => { addNotifRef.current = addNotification; }, [addNotification]);
  useEffect(() => { clearConvRef.current = clearConversation; }, [clearConversation]);

  useEffect(() => {
    if (tenants.length === 0) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined);
    }
  }, [tenants.length]);

  const tenantsKey = tenants.map((tenant) => tenant.id).join('|');

  useEffect(() => {
    if (!token || tenants.length === 0) return;
    const cleanups: Array<() => void> = [];

    const getTenantToken = async (tenantId: string) => {
      if (tenantId === currentTenant?.id) return token;
      const response = await fetch('/api/user-tenants/switch', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenantId }),
      });
      if (!response.ok) throw new Error(`Erro ao gerar token do tenant ${tenantId}`);
      const data = await response.json();
      if (!data?.token) throw new Error(`Token do tenant ${tenantId} nao retornado`);
      return data.token as string;
    };

    for (const tenant of tenants) {
      const tenantId = tenant.id;
      let tenantToken = token;
      let companyLabel = tenant.name || tenant.slug || 'Empresa';
      let closed = false;
      let reconnectTimer: number | null = null;
      const controller = new AbortController();
      const soundEnabledKeys = new Set<string>();

      const loadConfig = async () => {
        try {
          const [inboxesRes, settingsRes] = await Promise.all([
            fetch('/api/crm-chat/inboxes', { headers: { Authorization: `Bearer ${tenantToken}` }, signal: controller.signal }),
            fetch('/api/settings', { headers: { Authorization: `Bearer ${tenantToken}` }, signal: controller.signal }),
          ]);
          if (inboxesRes.ok) {
            const payload = await inboxesRes.json().catch(() => ({}));
            const inboxes: InboxLite[] = Array.isArray(payload?.data) ? payload.data : [];
            soundEnabledKeys.clear();
            for (const inbox of inboxes) {
              if (inbox.metadata?.soundNotification === true || inbox.metadata?.soundNotification === 'true') {
                soundEnabledKeys.add(`${inbox.provider}:${inbox.externalAccountId}`);
              }
            }
          }
          if (settingsRes.ok) {
            const sp = await settingsRes.json().catch(() => ({}));
            const name = sp?.data?.companyName || sp?.companyName;
            if (name) companyLabel = String(name).trim();
          }
        } catch {
          // fallback mantido
        }
      };

      const handlePayload = (payload: any) => {
        if (payload?.type === 'crm_chat_message') {
          if (payload.message?.direction !== 'incoming') return;
          const conversation = payload.conversation || {};
          const conversationId = conversation.id as string | undefined;
          if (!conversationId) return;

          addNotifRef.current(tenantId, conversationId, companyLabel);

          const accountKey = `${conversation.provider}:${conversation.externalAccountId}`;
          if (soundEnabledKeys.has(accountKey)) beep();

          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              const contactLabel = String(conversation.contactName || conversation.groupName || conversation.title || conversation.contactHandle || 'Nova mensagem').trim();
              const n = new Notification('Nova mensagem no Chat CRM', { body: `${companyLabel} - ${contactLabel}`, tag: `crm-${tenantId}-${conversationId}`, renotify: true } as NotificationOptions);
              n.onclick = () => { window.focus(); n.close(); };
            }
          } catch {
            // sem suporte
          }
        }

        if (payload?.type === 'crm_chat_conversation') {
          const conv = payload.conversation || {};
          if (conv.id && (conv.unreadCount === 0 || conv.unreadCount === '0')) {
            clearConvRef.current(tenantId, conv.id as string);
          }
        }
      };

      const connect = async () => {
        if (closed) return;
        try {
          tenantToken = await getTenantToken(tenantId);
          const response = await fetch('/api/crm-chat/events', {
            headers: { Authorization: `Bearer ${tenantToken}` },
            signal: controller.signal,
          });
          if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (!closed) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              if (!line.trim()) continue;
              try { handlePayload(JSON.parse(line)); } catch { /* linha invalida */ }
            }
          }
        } catch {
          // reconecta no finally
        } finally {
          if (!closed) reconnectTimer = window.setTimeout(connect, 5000);
        }
      };

      getTenantToken(tenantId).then((nextToken) => {
        tenantToken = nextToken;
        return loadConfig();
      }).catch(() => undefined);
      const configRefresh = window.setInterval(loadConfig, 120000);
      connect();

      cleanups.push(() => {
        closed = true;
        if (reconnectTimer) window.clearTimeout(reconnectTimer);
        window.clearInterval(configRefresh);
        controller.abort();
      });
    }

    return () => { cleanups.forEach((fn) => fn()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenantsKey, currentTenant?.id]);

  return null;
}
